import axios from 'axios';
import { api } from './api';
import type {
  AnsweredPercentageResponse,
  ConfigurationRow,
  ConfigurationSummary,
  DeleteConfigurationResponse,
  LayoutsResponse,
  SaveConfigurationRequest,
} from '@/types';

/** Errore sollevato dalla copia quando esiste già una configurazione confermata (HTTP 409). */
export class ConfigurationConflictError extends Error {}

export const configurationApi = {
  /**
   * Percentuale di avanzamento della configurazione (0..1, milestone-based; port di
   * Configuration_GetAnsweredPercentage). Il FE la mostra come progressbar (percentage*100).
   */
  getAnsweredPercentage: (configId: number): Promise<number> =>
    api
      .get<AnsweredPercentageResponse>(`/api/configurations/${configId}/answered-percentage`)
      .then((r) => r.data.percentage),

  /**
   * Riepilogo delle scelte per fase + prezzi + valore totale (Fase C).
   * NOTA: endpoint atteso `GET /api/configurations/:id/summary` — contratto anticipato dal peer BE,
   * da confermare quando è live.
   */
  getSummary: (configId: number): Promise<ConfigurationSummary> =>
    api
      .get<ConfigurationSummary>(`/api/configurations/${configId}/summary`)
      .then((r) => r.data),

  /** Varianti di planimetria selezionabili nel primo step di LAYOUT (Scelta Layout). */
  getLayouts: (configId: number): Promise<LayoutsResponse> =>
    api.get<LayoutsResponse>(`/api/configurations/${configId}/layouts`).then((r) => r.data),

  /** Imposta il layout (SetLayout): sblocca il menu del wizard. Azzera le risposte se cambia. */
  setLayout: (configId: number, layoutId: number): Promise<void> =>
    api.post(`/api/configurations/${configId}/layout`, { layoutId }).then(() => undefined),

  /** Crea una nuova configurazione per l'appartamento (LayoutId NON impostato, come il legacy). */
  create: (body: SaveConfigurationRequest): Promise<ConfigurationRow> =>
    api.post<ConfigurationRow>('/api/configurations', body).then((r) => r.data),

  /** Rinomina/aggiorna una configurazione. */
  rename: (id: number, body: SaveConfigurationRequest): Promise<ConfigurationRow> =>
    api.put<ConfigurationRow>(`/api/configurations/${id}`, body).then((r) => r.data),

  /** Soft-delete di una configurazione. */
  remove: (id: number): Promise<number> =>
    api
      .delete<DeleteConfigurationResponse>(`/api/configurations/${id}`)
      .then((r) => r.data.deletedId),

  /**
   * Duplica la configurazione ("Copia di …", con LayoutId + tutte le risposte).
   * Solleva `ConfigurationConflictError` se esiste già una configurazione confermata
   * per lo stesso appartamento (HTTP 409).
   */
  copy: (id: number): Promise<ConfigurationRow> =>
    api
      .post<ConfigurationRow>(`/api/configurations/${id}/copy`)
      .then((r) => r.data)
      .catch((error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          const message =
            (error.response.data as { message?: string } | undefined)?.message ??
            'Esiste già una configurazione confermata per questo appartamento.';
          throw new ConfigurationConflictError(message);
        }
        throw error;
      }),
};
